import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { CalendarDays, Check, X, AlertCircle, Coffee, Sparkles } from "lucide-react";

const CLASSES = ["Toddlers", "Early Years 1", "Early Years 2", "Early Years 3"];
const STATUS_OPTIONS = [
  { v: "present", label: "Present", icon: Check, cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { v: "absent", label: "Absent", icon: X, cls: "bg-rose-100 text-rose-700 border-rose-300" },
  { v: "late", label: "Late", icon: AlertCircle, cls: "bg-amber-100 text-amber-700 border-amber-300" },
  { v: "leave", label: "Leave", icon: Coffee, cls: "bg-sky-100 text-sky-700 border-sky-300" },
  { v: "holiday", label: "Holiday", icon: Sparkles, cls: "bg-violet-100 text-violet-700 border-violet-300" },
];

export default function AttendancePanel({ can }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [klass, setKlass] = useState("Toddlers");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marks, setMarks] = useState({}); // student_id -> status
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/attendance", { params: { date, klass } });
      setRows(res.data.students || []);
      const m = {};
      (res.data.students || []).forEach((s) => { if (s.status) m[s.id] = s.status; });
      setMarks(m);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [date, klass]); // eslint-disable-line

  const setAll = (status) => {
    const next = {};
    rows.forEach((s) => { next[s.id] = status; });
    setMarks(next);
  };

  const save = async () => {
    if (!can("manage_attendance")) return toast.error("You don't have permission to mark attendance");
    const payloadMarks = Object.entries(marks).map(([sid, st]) => ({ student_id: sid, status: st }));
    if (payloadMarks.length === 0) return toast.info("No marks to save");
    setSaving(true);
    try {
      await adminApi.post("/admin/attendance/bulk", { date, marks: payloadMarks });
      toast.success(`Saved attendance for ${payloadMarks.length} student${payloadMarks.length === 1 ? "" : "s"}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => {
    const c = { present: 0, absent: 0, late: 0, leave: 0, holiday: 0, unmarked: 0 };
    rows.forEach((s) => {
      const m = marks[s.id];
      if (!m) c.unmarked += 1;
      else if (c[m] !== undefined) c[m] += 1;
    });
    return c;
  }, [rows, marks]);

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="attendance-panel">
      <div className="p-5 md:p-7 border-b border-[#0F1B47]/8 flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl text-[#0F1B47] font-medium">Attendance</h2>
            <p className="text-sm text-[#4A5568] mt-1">Mark daily attendance for one class at a time.</p>
          </div>
          {can("manage_attendance") && (
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50"
              data-testid="attendance-save"
            >
              {saving ? "Saving…" : "Save attendance"}
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <CalendarDays size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-[#F5F5F0] border border-[#0F1B47]/10 rounded-full pl-9 pr-4 py-2.5 text-sm focus:outline-none"
              data-testid="attendance-date"
            />
          </div>
          <select
            value={klass}
            onChange={(e) => setKlass(e.target.value)}
            className="bg-[#F5F5F0] border border-[#0F1B47]/10 rounded-full px-4 py-2.5 text-sm focus:outline-none"
            data-testid="attendance-class"
          >
            {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {can("manage_attendance") && rows.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 md:ml-auto">
              <span className="text-xs text-[#718096]">Quick:</span>
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setAll(s.v)}
                  className={`text-[11px] font-semibold rounded-full px-3 py-1.5 border ${s.cls}`}
                  data-testid={`attendance-mark-all-${s.v}`}
                >
                  All {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 text-[11px] font-bold tracking-[0.16em] uppercase">
          <Pill cls="bg-emerald-100 text-emerald-700">Present {summary.present}</Pill>
          <Pill cls="bg-rose-100 text-rose-700">Absent {summary.absent}</Pill>
          <Pill cls="bg-amber-100 text-amber-700">Late {summary.late}</Pill>
          <Pill cls="bg-sky-100 text-sky-700">Leave {summary.leave}</Pill>
          <Pill cls="bg-violet-100 text-violet-700">Holiday {summary.holiday}</Pill>
          <Pill cls="bg-[#F5F5F0] text-[#4A5568]">Unmarked {summary.unmarked}</Pill>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#718096] text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-[#718096] text-sm">No active students in {klass} yet.</div>
      ) : (
        <ul data-testid="attendance-list">
          {rows.map((s) => (
            <li key={s.id} className="border-t border-[#0F1B47]/8 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Avatar url={s.photo_url} name={s.full_name} />
                <div className="min-w-0">
                  <div className="text-[11px] font-mono text-[#718096] tracking-wide">{s.admission_id}</div>
                  <div className="font-semibold text-[#0F1B47] text-[14.5px] truncate">{s.full_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {STATUS_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = marks[s.id] === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      disabled={!can("manage_attendance")}
                      onClick={() => setMarks((m) => ({ ...m, [s.id]: opt.v }))}
                      className={`text-[11px] font-semibold rounded-full px-3 py-1.5 border inline-flex items-center gap-1 ${active ? opt.cls : "bg-[#FDFDF9] text-[#0F1B47] border-[#0F1B47]/15 hover:border-[#0F1B47]/40"} ${!can("manage_attendance") ? "opacity-60 cursor-not-allowed" : ""}`}
                      data-testid={`attendance-${s.id}-${opt.v}`}
                    >
                      <Icon size={11} /> {opt.label}
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Pill({ children, cls }) {
  return <span className={`px-2.5 py-1 rounded-full ${cls}`}>{children}</span>;
}
function Avatar({ url, name }) {
  if (url) return <img src={url} alt={name} className="w-10 h-10 rounded-xl object-cover" loading="lazy" decoding="async" />;
  const initials = (name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return <div className="w-10 h-10 rounded-xl bg-[#E3EDF9] text-[#0F1B47] font-semibold flex items-center justify-center text-sm">{initials}</div>;
}
